package br.gov.go.pge.permanenciaweb.site.pages.admin

import br.gov.go.pge.permanenciaweb.shared.AccessGrant
import br.gov.go.pge.permanenciaweb.shared.AccessPreset
import br.gov.go.pge.permanenciaweb.shared.AdminUserDto
import br.gov.go.pge.permanenciaweb.shared.Capability
import br.gov.go.pge.permanenciaweb.shared.GrantDto
import br.gov.go.pge.permanenciaweb.shared.GrantScope
import br.gov.go.pge.permanenciaweb.shared.grants

/*
 * pt-BR copy for the access editor. Capability ids and preset definitions live in `:shared`, so the
 * server and the site cannot drift; only the human-facing strings live here.
 */

internal enum class AccessChipTone { NORMAL, WARNING, DANGER, MUTED }

/** Shared tooltip for every control that would remove the last active administrator. */
internal const val LAST_ADMIN_REMOVAL_BLOCKED = "Não é possível remover acesso: último admin"

/** Strings for the destructive confirmation dialog; [selfWarning] is rendered in red. */
internal data class ConfirmCopy(
    val title: String,
    val message: String,
    val confirmLabel: String,
    val selfWarning: String? = null,
)

internal fun denyAccessConfirmation(username: String, isSelf: Boolean): ConfirmCopy =
    if (isSelf) {
        ConfirmCopy(
            title = "Negar o acesso da sua própria conta?",
            message = "Sua conta deixará de ter acesso e você será desconectado. As permissões " +
                "ficam suspensas até que outro administrador reative a conta.",
            confirmLabel = "Negar meu acesso",
            selfWarning = "ATENÇÃO: esta ação desativa a sua própria conta.",
        )
    } else {
        ConfirmCopy(
            title = "Negar o acesso de $username?",
            message = "A conta deixará de ter acesso e as permissões ficam suspensas até a reativação.",
            confirmLabel = "Negar acesso",
        )
    }

/**
 * Copy for removing `MANAGE_ACCESS` on save. [keepsReport] only matters for the self case: it decides
 * whether the user will keep access to the panel after demoting themselves.
 */
internal fun removeAdminConfirmation(
    username: String,
    isSelf: Boolean,
    keepsReport: Boolean,
): ConfirmCopy =
    if (isSelf) {
        ConfirmCopy(
            title = "Remover seu acesso de administrador?",
            message = if (keepsReport) {
                "Você deixará de aprovar contas e editar permissões, mas continuará com acesso " +
                    "ao painel. Se precisar, outro administrador poderá restaurar seu acesso."
            } else {
                "Você deixará de aprovar contas e editar permissões. Como 'Ver painel' também " +
                    "será removido, você perderá o acesso ao painel após salvar. Se precisar, " +
                    "outro administrador poderá restaurar seu acesso."
            },
            confirmLabel = "Remover meu acesso",
            selfWarning = "ATENÇÃO: esta ação afeta o seu próprio acesso administrativo.",
        )
    } else {
        ConfirmCopy(
            title = "Remover o acesso de administrador de $username?",
            message = "A conta continuará ativa, mas perderá a gestão de usuários e permissões.",
            confirmLabel = "Remover acesso",
        )
    }

internal val PRESET_LABELS: Map<AccessPreset, String> = mapOf(
    AccessPreset.USER to "Usuário",
    AccessPreset.LEADERSHIP to "Chefia",
    AccessPreset.ADMIN to "Administrador",
)

internal fun presetLabel(preset: AccessPreset): String = PRESET_LABELS[preset] ?: preset.name

internal val PRESET_HELP: Map<AccessPreset, String> = mapOf(
    AccessPreset.USER to "Painel e correções do painel. O relatório é liberado à parte.",
    AccessPreset.LEADERSHIP to "Gerencia as próprias liberações. O relatório é liberado à parte.",
    AccessPreset.ADMIN to "Acesso total, incluindo gerenciar acessos.",
)

internal fun capabilityLabel(capability: Capability): String = when (capability) {
    Capability.VIEW_REPORT -> "Ver painel"
    Capability.MANAGE_ROSTER_CORRECTIONS -> "Corrigir painel"
    Capability.CREATE_STAY_PERMIT -> "Criar liberações"
    Capability.VIEW_STAY_PERMITS -> "Ver liberações"
    Capability.REVOKE_STAY_PERMITS -> "Revogar liberações"
    Capability.MANAGE_ACCESS -> "Gerenciar acessos"
}

/** Short one-liners shown under each capability in the editor. */
internal fun capabilityHelp(capability: Capability): String = when (capability) {
    Capability.VIEW_REPORT -> "Painel, eventos e histórico de remoções."
    Capability.MANAGE_ROSTER_CORRECTIONS -> "Saída virtual no painel. Exige 'Ver painel'."
    Capability.CREATE_STAY_PERMIT -> "Formulário de autorizações de permanência."
    Capability.VIEW_STAY_PERMITS -> "'Próprias': as suas. 'Todas': de todas as chefias."
    Capability.REVOKE_STAY_PERMITS -> "'Próprias': as suas. 'Todas': de qualquer pessoa."
    Capability.MANAGE_ACCESS -> "Aprova contas e edita qualquer permissão, inclusive a própria."
}

internal fun scopeLabel(scope: GrantScope): String = when (scope) {
    GrantScope.GLOBAL -> "—"
    GrantScope.OWN -> "Próprias"
    GrantScope.ALL -> "Todas"
}

/** Parses a wire grant, ignoring anything the server should never send. */
internal fun GrantDto.toGrantOrNull(): AccessGrant? = runCatching {
    AccessGrant(Capability.valueOf(capability), GrantScope.valueOf(scope))
}.getOrNull()

internal fun List<GrantDto>.toGrantSet(): Set<AccessGrant> = mapNotNull { it.toGrantOrNull() }.toSet()

internal fun List<GrantDto>.syncGrantSet(): Set<AccessGrant> =
    filter { it.source == "SYNC" }.mapNotNull { it.toGrantOrNull() }.toSet()

internal fun List<GrantDto>.has(capability: Capability, scope: GrantScope = GrantScope.GLOBAL): Boolean =
    any {
        it.capability == capability.name && it.scope == scope.name && it.revokedAt == null
    }

/** Compact chips for the active table. */
internal fun permissionChips(grants: List<GrantDto>): List<Pair<String, AccessChipTone>> {
    val set = grants.toGrantSet()
    if (set.isEmpty()) return listOf("Sem acesso" to AccessChipTone.MUTED)
    if (set.grants(Capability.MANAGE_ACCESS)) return listOf("Administração" to AccessChipTone.DANGER)

    val chips = mutableListOf<Pair<String, AccessChipTone>>()
    if (set.grants(Capability.VIEW_REPORT)) chips += "Painel" to AccessChipTone.NORMAL
    if (set.grants(Capability.MANAGE_ROSTER_CORRECTIONS)) chips += "Correções" to AccessChipTone.NORMAL

    val create = set.grants(Capability.CREATE_STAY_PERMIT)
    val viewOwn = set.grants(Capability.VIEW_STAY_PERMITS, GrantScope.OWN)
    val viewAll = set.grants(Capability.VIEW_STAY_PERMITS, GrantScope.ALL)
    val revokeOwn = set.grants(Capability.REVOKE_STAY_PERMITS, GrantScope.OWN)
    val revokeAll = set.grants(Capability.REVOKE_STAY_PERMITS, GrantScope.ALL)

    when {
        create && (viewOwn || viewAll) && (revokeOwn || revokeAll) ->
            chips += "Liberações · ${if (viewAll || revokeAll) "todas" else "próprias"}" to AccessChipTone.NORMAL

        create -> chips += "Criar liberações" to AccessChipTone.NORMAL
        else -> {
            if (viewAll) chips += "Ver liberações · todas" to AccessChipTone.NORMAL
            else if (viewOwn) chips += "Ver liberações · próprias" to AccessChipTone.NORMAL
            if (revokeAll) chips += "Revogar · todas" to AccessChipTone.NORMAL
            else if (revokeOwn) chips += "Revogar · próprias" to AccessChipTone.NORMAL
        }
    }
    return chips
}

/** Plain-language effective permissions, used in the editor summary. */
internal fun permissionLines(grants: Set<AccessGrant>): List<String> {
    if (grants.isEmpty()) return listOf("Nenhuma permissão — a conta entra, mas não vê nenhuma página.")
    val lines = mutableListOf<String>()
    if (grants.grants(Capability.VIEW_REPORT)) lines += "ver o painel"
    if (grants.grants(Capability.MANAGE_ROSTER_CORRECTIONS)) lines += "corrigir o painel"
    if (grants.grants(Capability.CREATE_STAY_PERMIT)) lines += "criar liberações"
    when {
        grants.grants(Capability.VIEW_STAY_PERMITS, GrantScope.ALL) -> lines += "ver todas as liberações"
        grants.grants(Capability.VIEW_STAY_PERMITS, GrantScope.OWN) -> lines += "ver as próprias liberações"
    }
    when {
        grants.grants(Capability.REVOKE_STAY_PERMITS, GrantScope.ALL) -> lines += "revogar qualquer liberação"
        grants.grants(Capability.REVOKE_STAY_PERMITS, GrantScope.OWN) -> lines += "revogar as próprias liberações"
    }
    if (grants.grants(Capability.MANAGE_ACCESS)) lines += "gerenciar acessos"
    return lines
}

internal enum class UserFilter(val label: String) {
    ALL("Todos"),
    NO_ACCESS("Sem acesso"),
    NO_REPORT("Sem relatório"),
    ADMINS("Administradores"),
    LEADERSHIP("Chefias");

    fun matches(user: AdminUserDto): Boolean = when (this) {
        ALL -> true
        NO_ACCESS -> user.grants.isEmpty()
        NO_REPORT -> !user.grants.has(Capability.VIEW_REPORT)
        ADMINS -> user.grants.has(Capability.MANAGE_ACCESS)
        LEADERSHIP -> user.isLeadership
    }
}

internal fun statusLabel(status: String): String = when (status) {
    "PENDING" -> "Pendente"
    "ACTIVE" -> "Ativa"
    "DENIED" -> "Negada"
    else -> status
}

internal fun statusChipTone(status: String): AccessChipTone = when (status) {
    "PENDING" -> AccessChipTone.WARNING
    "DENIED" -> AccessChipTone.DANGER
    else -> AccessChipTone.NORMAL
}

/** "22/10/2026 15:55" -> "22/10 15:55" (o ano não cabe numa lista compacta); outros formatos passam. */
internal fun shortStamp(stamp: String?): String {
    val s = stamp.orEmpty()
    return if (s.length == 16 && s[2] == '/' && s[5] == '/' && s[10] == ' ') {
        s.take(5) + s.takeLast(6)
    } else {
        s
    }
}

internal fun sourceLabel(source: String?): String = when (source) {
    "SYNC" -> "sincronizado"
    "PRESET" -> "perfil"
    "MIGRATION" -> "migração"
    else -> "explícito"
}

internal fun runStatusLabel(status: String): String = when (status) {
    "PENDING" -> "Pendente"
    "RUNNING" -> "Em andamento"
    "DONE" -> "Concluída"
    "FAILED" -> "Falhou"
    else -> status
}
